import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Send, Users, Globe, CheckCircle, Sparkles, Target } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AIWriteModal } from './AIWriteModal';
import { OrganizationMultiSelect } from './OrganizationMultiSelect';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface NotificationResult {
  success: boolean;
  notifications_created: number;
  users_processed: number;
  users_skipped: number;
  error_count: number;
  errors?: string[];
  summary: string;
}

export const GlobalSystemNotifications: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    type: 'system',
    title: '',
    message: '',
    priority: 'medium',
    category: 'general',
  });
  
  const [targetType, setTargetType] = useState<'all' | 'specific'>('all');
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<NotificationResult | null>(null);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAINotificationGenerated = (title: string, message: string) => {
    setFormData(prev => ({
      ...prev,
      title: title,
      message: message
    }));
  };

  const handleSendNotification = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title and message are required.',
        variant: 'destructive',
      });
      return;
    }

    if (targetType === 'specific' && selectedOrganizations.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one organization.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    setLastResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('create-global-system-notifications', {
        body: {
          type: formData.type,
          title: formData.title,
          message: formData.message,
          target_type: targetType,
          target_organizations: targetType === 'specific' ? selectedOrganizations : undefined,
          data: {
            priority: formData.priority,
            category: formData.category,
            created_from: 'admin_panel',
            timestamp: new Date().toISOString()
          }
        }
      });

      if (error) {
        throw error;
      }

      setLastResult(data as NotificationResult);

      toast({
        title: 'Global Notification Sent!',
        description: `Successfully sent to ${data.notifications_created} users across all organizations.`,
        duration: 5000,
      });

      // Clear form after successful send
      setFormData({
        type: 'system',
        title: '',
        message: '',
        priority: 'medium',
        category: 'general',
      });

    } catch (error) {
      console.error('Error sending global notification:', error);
      toast({
        title: 'Error',
        description: `Failed to send notification: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Warning Alert */}
      <Alert className="border-orange-200 bg-orange-50">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-orange-800">
          <strong>Super Admin Only:</strong> This will send notifications to ALL users across ALL organizations. 
          Use with caution as this cannot be undone.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Global System Notifications
          </CardTitle>
          <CardDescription>
            Send system notifications to all users across all organizations in the platform.
            Users can still disable system notifications in their preferences.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Notification Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Notification Type</Label>
            <Select 
              value={formData.type} 
              onValueChange={(value) => handleInputChange('type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">System Notification</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority Level</Label>
            <Select 
              value={formData.priority} 
              onValueChange={(value) => handleInputChange('priority', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="critical">🔴 Critical</SelectItem>
                <SelectItem value="high">🟠 High</SelectItem>
                <SelectItem value="medium">🔵 Medium</SelectItem>
                <SelectItem value="low">⚪ Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select 
              value={formData.category} 
              onValueChange={(value) => handleInputChange('category', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="feature">New Feature</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="policy">Policy Update</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Target Organizations */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Target Organizations</Label>
            <RadioGroup
              value={targetType}
              onValueChange={(value: 'all' | 'specific') => {
                setTargetType(value);
                if (value === 'all') {
                  setSelectedOrganizations([]);
                }
              }}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="target-all" />
                <Label htmlFor="target-all" className="flex items-center gap-2 cursor-pointer">
                  <Globe className="h-4 w-4 text-blue-500" />
                  All Organizations
                  <span className="text-sm text-muted-foreground">(Send to all users across all organizations)</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="target-specific" />
                <Label htmlFor="target-specific" className="flex items-center gap-2 cursor-pointer">
                  <Target className="h-4 w-4 text-orange-600" />
                  Specific Organizations
                  <span className="text-sm text-muted-foreground">(Send to selected organizations only)</span>
                </Label>
              </div>
            </RadioGroup>
            
            {targetType === 'specific' && (
              <div className="ml-6 space-y-2">
                <Label>Select Organizations</Label>
                <OrganizationMultiSelect
                  selectedOrganizations={selectedOrganizations}
                  onSelectionChange={setSelectedOrganizations}
                  disabled={isLoading}
                />
                {selectedOrganizations.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {selectedOrganizations.length} organization{selectedOrganizations.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Notification Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Scheduled Maintenance Tonight"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              {formData.title.length}/100 characters
            </p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="message">Notification Message *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsAIModalOpen(true)}
                className="text-xs"
              >
                <Sparkles className="mr-1 h-3 w-3" />
                Write with AI
              </Button>
            </div>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="e.g., The platform will be unavailable from 2:00 AM to 4:00 AM EST for scheduled maintenance. We apologize for any inconvenience."
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {formData.message.length}/500 characters
            </p>
          </div>

          {/* Preview */}
          {(formData.title || formData.message) && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <Card className="border-dashed">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-lg">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-bold leading-tight">
                          {formData.title || 'Notification Title'}
                        </h4>
                        <div className="flex items-center gap-1">
                          <Badge className={getPriorityColor(formData.priority)}>
                            {formData.priority}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm mt-1 text-muted-foreground">
                        {formData.message || 'Notification message will appear here...'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Just now • System
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Send Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSendNotification}
              disabled={isLoading || !formData.title.trim() || !formData.message.trim()}
              className="min-w-32"
            >
              {isLoading ? (
                <>
                  <Users className="mr-2 h-4 w-4 animate-pulse" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send to All Users
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {lastResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Notification Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {lastResult.notifications_created}
                </div>
                <div className="text-sm text-muted-foreground">Sent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-500">
                  {lastResult.users_processed}
                </div>
                <div className="text-sm text-muted-foreground">Processed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {lastResult.users_skipped}
                </div>
                <div className="text-sm text-muted-foreground">Skipped</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {lastResult.error_count}
                </div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {lastResult.summary}
            </p>

            {lastResult.errors && lastResult.errors.length > 0 && (
              <div className="mt-4">
                <Label className="text-red-600">Errors (showing first 10):</Label>
                <div className="mt-2 space-y-1">
                  {lastResult.errors.slice(0, 10).map((error, index) => (
                    <p key={index} className="text-xs text-red-600 font-mono bg-red-50 p-2 rounded">
                      {error}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AIWriteModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onNotificationGenerated={handleAINotificationGenerated}
        category={formData.category}
        priority={formData.priority}
      />
    </div>
  );
};
